import type {
  LexicalSearchAttributeFilter,
  LexicalSearchFilter,
} from '@atlassiansox/code-search-query-parser';

/**
 * Combines a top-level scope filter (e.g. `namespace:<workspace>`) with a
 * user-supplied filter, returning a single `LexicalSearchFilter` whose root
 * is always an AND with the scope filter in the first position.
 *
 * Two cases are handled:
 *
 *   1. The user filter is itself an AND — its children are flattened into
 *      the result so we don't end up with a nested `and` node:
 *      `{ and: [scopeFilter, ...userFilter.and] }`.
 *   2. Anything else (leaf equality, OR, NOT, etc.) is wrapped as-is:
 *      `{ and: [scopeFilter, userFilter] }`.
 *
 * A `userFilter` is always required; callers that have no user filter should
 * pass the scope filter through directly rather than invoking this helper.
 *
 * Placing the scope filter first lets downstream query builders rely on a
 * consistent ordering when serialising the AST.
 */
export function composeScopedFilter(
  scopeFilter: LexicalSearchAttributeFilter,
  userFilter: LexicalSearchFilter
): LexicalSearchFilter {
  return 'and' in userFilter
    ? { and: [scopeFilter, ...userFilter.and] }
    : { and: [scopeFilter, userFilter] };
}
